import React, { useEffect, useMemo, useState } from 'react';
import { ChatCircleText, MagnifyingGlass, PencilSimple, Plus, Trash, UsersThree } from '@phosphor-icons/react';
import { useNavigate } from 'react-router-dom';
import { api } from '@/lib/api';
import { apiErrorMessage, cn, fmtDate } from '@/lib/utils';
import { Badge, Button, EmptyState, Input, Spinner, useToast } from '@/components/ui-kit';
import { useInstances } from '@/context/InstanceContext';

function initials(name, phone) {
  const source = (name && name !== phone ? name : phone) || '?';
  return source
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part[0])
    .join('')
    .toUpperCase();
}

function stageTone(stage) {
  if (stage === 'fechado') return 'success';
  if (stage === 'perdido') return 'danger';
  if (stage === 'agendado') return 'warn';
  return 'info';
}

export default function CrmPage() {
  const { instances, current } = useInstances();
  const navigate = useNavigate();
  const [stages, setStages] = useState([]);
  const [contacts, setContacts] = useState([]);
  const [draggingId, setDraggingId] = useState('');
  const [instanceName, setInstanceName] = useState('');
  const [query, setQuery] = useState('');
  const [loading, setLoading] = useState(false);
  const [editingPipeline, setEditingPipeline] = useState(false);
  const [stageDrafts, setStageDrafts] = useState([]);
  const [savingStages, setSavingStages] = useState(false);
  const toast = useToast();

  const defaultInstance = current || instances[0]?.instanceName || '';
  const activeInstance = instanceName === '__all__' ? '' : (instanceName || defaultInstance);
  const instanceSelectValue = instanceName === '__all__' ? '__all__' : activeInstance;

  const load = async () => {
    setLoading(true);
    try {
      const [stageRows, contactRows] = await Promise.all([
        api.crmStages(),
        api.crmContacts({ instanceName: activeInstance || undefined, q: query || undefined }),
      ]);
      setStages(stageRows);
      setContacts(contactRows);
    } catch (e) {
      toast.show(apiErrorMessage(e), 'error');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeInstance, query]);

  const byStage = useMemo(() => {
    const grouped = {};
    for (const stage of stages) grouped[stage.id] = [];
    for (const contact of contacts) {
      const key = grouped[contact.stage] ? contact.stage : stages[0]?.id;
      if (!key) continue;
      grouped[key] = grouped[key] || [];
      grouped[key].push(contact);
    }
    return grouped;
  }, [contacts, stages]);

  const openPipelineEditor = () => {
    setStageDrafts(stages.map((stage) => ({ ...stage })));
    setEditingPipeline(true);
  };

  const savePipeline = async () => {
    const clean = stageDrafts
      .map((stage) => ({ id: stage.id, label: (stage.label || '').trim() }))
      .filter((stage) => stage.label);
    if (!clean.length) {
      toast.show('Mantenha pelo menos uma etapa na pipeline', 'error');
      return;
    }
    setSavingStages(true);
    try {
      const updated = await api.updateCrmStages({ stages: clean });
      setStages(updated);
      setEditingPipeline(false);
      await load();
      toast.show('Pipeline atualizada', 'success');
    } catch (e) {
      toast.show(apiErrorMessage(e), 'error');
    } finally {
      setSavingStages(false);
    }
  };

  const openConversation = (contact) => {
    if (!contact?.id) return;
    const params = new URLSearchParams({ conversation: contact.id });
    if (contact.instanceName || activeInstance) {
      params.set('instanceName', contact.instanceName || activeInstance);
    }
    navigate(`/inbox?${params.toString()}`);
  };

  const moveContactToStage = async (contact, stageId) => {
    if (!contact?.id || !stageId || contact.stage === stageId) return;
    const previous = contacts;
    setContacts((current) => current.map((item) => item.id === contact.id ? { ...item, stage: stageId } : item));
    try {
      await api.updateCrmContact(contact.id, { stage: stageId });
      const stage = stages.find((item) => item.id === stageId);
      toast.show(`Lead movido para ${stage?.label || 'nova etapa'}`, 'success');
    } catch (e) {
      setContacts(previous);
      toast.show(apiErrorMessage(e), 'error');
    }
  };

  const totalContacts = contacts.length;
  const taggedContacts = contacts.filter((contact) => (contact.tags || []).length).length;
  const activeStages = stages.filter((stage) => (byStage[stage.id] || []).length).length;
  const lastContactAt = contacts
    .map((contact) => contact.lastSeenAt)
    .filter(Boolean)
    .sort()
    .at(-1);

  return (
    <div className="space-y-8">
      {toast.node}
      <header className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <div className="eyebrow">// PIPELINE DE RELACIONAMENTO</div>
          <h1 className="page-title">CRM</h1>
          <p className="mt-2 max-w-2xl" style={{ color: 'var(--s1-text-2)' }}>
            Organize leads por etapa e acompanhe o atendimento iniciado no Inbox.
          </p>
        </div>
        <div className="flex w-full flex-wrap gap-2 sm:w-auto">
          <select
            value={instanceSelectValue}
            onChange={(e) => setInstanceName(e.target.value)}
            className="w-full rounded-[var(--s1-radius-control)] border px-3 py-2 text-sm sm:w-auto sm:min-w-[220px]"
            style={{ background: 'var(--s1-input)', borderColor: 'var(--s1-card-border)', color: 'var(--s1-text)' }}
          >
            <option value="__all__">Todas as instâncias</option>
            {instances.map((item) => (
              <option key={item.instanceName} value={item.instanceName}>{item.instanceName}</option>
            ))}
          </select>
          <Button type="button" variant="secondary" onClick={openPipelineEditor}>
            <PencilSimple size={16} /> Editar pipeline
          </Button>
          <Button type="button" variant="secondary" onClick={load}>Atualizar</Button>
        </div>
      </header>

      {editingPipeline && (
        <section
          className="rounded-[var(--s1-radius-panel)] border p-5"
          style={{ borderColor: 'var(--s1-card-border)', background: 'var(--s1-panel)' }}
        >
          <div className="flex flex-wrap items-start justify-between gap-4">
            <div>
              <h2 className="section-title">Etapas da pipeline</h2>
              <p className="mt-1 text-sm" style={{ color: 'var(--s1-text-2)' }}>
                Edite a ordem e os nomes das etapas que aparecem no CRM e no Inbox.
              </p>
            </div>
            <Button
              type="button"
              variant="secondary"
              onClick={() => setStageDrafts((prev) => [...prev, { id: '', label: 'Nova etapa' }])}
            >
              <Plus size={16} /> Nova etapa
            </Button>
          </div>
          <div className="mt-5 grid gap-3 md:grid-cols-2 xl:grid-cols-3">
            {stageDrafts.map((stage, index) => (
              <div key={`${stage.id || 'new'}-${index}`} className="flex items-center gap-2 rounded-[var(--s1-radius-card)] border p-2" style={{ borderColor: 'var(--s1-card-border)', background: 'var(--s1-card)' }}>
                <Input
                  value={stage.label}
                  onChange={(e) => setStageDrafts((prev) => prev.map((item, itemIndex) => itemIndex === index ? { ...item, label: e.target.value } : item))}
                  placeholder="Nome da etapa"
                />
                <Button
                  type="button"
                  variant="ghost"
                  size="icon"
                  disabled={stageDrafts.length <= 1}
                  onClick={() => setStageDrafts((prev) => prev.filter((_, itemIndex) => itemIndex !== index))}
                  title="Remover etapa"
                >
                  <Trash size={16} />
                </Button>
              </div>
            ))}
          </div>
          <div className="mt-5 flex flex-wrap justify-end gap-2">
            <Button type="button" variant="secondary" onClick={() => setEditingPipeline(false)}>Cancelar</Button>
            <Button type="button" onClick={savePipeline} disabled={savingStages}>
              {savingStages ? <Spinner /> : 'Salvar pipeline'}
            </Button>
          </div>
        </section>
      )}

      <div className="relative max-w-xl">
        <MagnifyingGlass size={16} className="absolute left-3 top-1/2 -translate-y-1/2" style={{ color: 'var(--s1-muted)' }} />
        <Input className="pl-9" value={query} onChange={(e) => setQuery(e.target.value)} placeholder="buscar por nome, telefone ou tag" />
      </div>

      <section className="grid gap-3 md:grid-cols-4">
        {[
          ['Leads no CRM', totalContacts],
          ['Etapas com leads', activeStages],
          ['Com tags', taggedContacts],
          ['Última atividade', lastContactAt ? fmtDate(lastContactAt) : '-'],
        ].map(([label, value]) => (
          <div
            key={label}
            className="rounded-[var(--s1-radius-card)] border p-4"
            style={{ borderColor: 'var(--s1-card-border)', background: 'var(--s1-card)' }}
          >
            <p className="text-[10px] uppercase tracking-[0.16em]" style={{ color: 'var(--s1-muted)' }}>{label}</p>
            <strong className="mt-2 block text-2xl" style={{ color: 'var(--s1-text)' }}>{value}</strong>
          </div>
        ))}
      </section>

      <section>
        <div className="min-h-[640px] min-w-0 overflow-x-auto rounded-[var(--s1-radius-panel)] border p-3 sm:p-4" style={{ borderColor: 'var(--s1-card-border)', background: 'var(--s1-panel)' }}>
          {loading && !contacts.length ? (
            <div className="grid h-[520px] place-items-center"><Spinner /></div>
          ) : stages.length ? (
            <div
              className="grid min-w-[920px] gap-3 sm:min-w-[1120px] xl:min-w-[1280px]"
              style={{ gridTemplateColumns: `repeat(${Math.max(stages.length, 1)}, minmax(220px, 1fr))` }}
            >
              {stages.map((stage) => (
                <div
                  key={stage.id}
                  onDragOver={(event) => event.preventDefault()}
                  onDrop={(event) => {
                    event.preventDefault();
                    const contactId = event.dataTransfer.getData('text/plain') || draggingId;
                    const contact = contacts.find((item) => item.id === contactId);
                    setDraggingId('');
                    moveContactToStage(contact, stage.id);
                  }}
                  className="rounded-[var(--s1-radius-card)] border"
                  style={{ borderColor: draggingId ? 'var(--s1-accent)' : 'var(--s1-card-border)', background: 'var(--s1-card)' }}
                >
                  <div className="flex items-center justify-between border-b p-3" style={{ borderColor: 'var(--s1-line)' }}>
                    <h2 className="text-sm font-semibold" style={{ color: 'var(--s1-text)' }}>{stage.label}</h2>
                    <Badge variant={stageTone(stage.id)}>{byStage[stage.id]?.length || 0}</Badge>
                  </div>
                  <div className="max-h-[560px] space-y-2 overflow-y-auto p-2">
                    {(byStage[stage.id] || []).map((contact) => (
                      <div
                        key={contact.id}
                        draggable
                        onDragStart={(event) => {
                          event.dataTransfer.setData('text/plain', contact.id);
                          event.dataTransfer.effectAllowed = 'move';
                          setDraggingId(contact.id);
                        }}
                        onDragEnd={() => setDraggingId('')}
                        className={cn('w-full cursor-grab rounded-[var(--s1-radius-card)] border p-3 text-left transition active:cursor-grabbing', draggingId === contact.id && 'opacity-60')}
                        style={{
                          background: 'var(--s1-panel)',
                          borderColor: 'var(--s1-card-border)',
                        }}
                      >
                        <button type="button" onClick={() => openConversation(contact)} className="w-full text-left">
                          <div className="flex items-center gap-2">
                            <span className="grid h-9 w-9 shrink-0 place-items-center rounded-full border text-xs font-semibold" style={{ borderColor: 'var(--s1-gold)', color: 'var(--s1-gold-text)' }}>
                              {initials(contact.name, contact.phone)}
                            </span>
                            <span className="min-w-0">
                              <strong className="block truncate text-sm" style={{ color: 'var(--s1-text)' }}>{contact.name || contact.phone}</strong>
                              <span className="block truncate text-xs" style={{ color: 'var(--s1-muted)' }}>{contact.phone}</span>
                            </span>
                          </div>
                        </button>
                        {!!contact.tags?.length && (
                          <div className="mt-3 flex flex-wrap gap-1">
                            {contact.tags.slice(0, 3).map((tag) => <Badge key={tag}>{tag}</Badge>)}
                          </div>
                        )}
                        <div className="mt-3 flex items-center justify-between gap-2">
                          <p className="text-[11px]" style={{ color: 'var(--s1-muted)' }}>visto em {fmtDate(contact.lastSeenAt)}</p>
                          <button
                            type="button"
                            onClick={() => openConversation(contact)}
                            className="inline-flex items-center gap-1 text-[11px] font-semibold"
                            style={{ color: 'var(--s1-accent-text)' }}
                          >
                            <PencilSimple size={12} /> Inbox
                          </button>
                        </div>
                        <button
                          type="button"
                          onClick={() => openConversation(contact)}
                          className="mt-3 inline-flex w-full items-center justify-center gap-2 rounded-[var(--s1-radius-control)] border px-3 py-2 text-xs font-semibold"
                          style={{ borderColor: 'var(--s1-card-border)', color: 'var(--s1-text)', background: 'var(--s1-input)' }}
                        >
                          <ChatCircleText size={15} /> Abrir conversa
                        </button>
                      </div>
                    ))}
                    {!byStage[stage.id]?.length && (
                      <div
                        className="grid min-h-[180px] place-items-center rounded-[var(--s1-radius-card)] border border-dashed p-4 text-center text-sm"
                        style={{ borderColor: 'var(--s1-line)', color: 'var(--s1-muted)', background: 'var(--s1-panel)' }}
                      >
                        Nenhum lead nesta etapa.
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <EmptyState icon={UsersThree} title="Nenhum lead no CRM" hint="Quando o Inbox receber conversas privadas, os contatos aparecerão aqui." />
          )}
        </div>
      </section>
    </div>
  );
}
